import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { History } from 'lucide-react';
import {
  getSpreadsheetActivity,
  type AuditLogEntryWithProfile,
} from '@/lib/activity-log';
import WidgetCard, { EmptyLine } from './WidgetCard';

const VERBS: Record<string, string> = {
  create: 'added',
  insert: 'added',
  update: 'updated',
  delete: 'removed',
  restore_row: 'restored',
  restore_cell: 'restored',
  clear_cell: 'cleared',
};

interface ActivityCardProps {
  /** The selected workflow month's sheet id */
  monthId: string | null;
  /** Bumped by the page on every data refresh, so the feed follows */
  refreshKey: number;
}

/** The team's latest edits in the selected workflow month */
export default function ActivityCard({
  monthId,
  refreshKey,
}: Readonly<ActivityCardProps>) {
  const [entries, setEntries] = useState<AuditLogEntryWithProfile[] | null>(
    null
  );
  const [problem, setProblem] = useState(false);

  useEffect(() => {
    if (!monthId) return;
    let alive = true;
    getSpreadsheetActivity(monthId, 8)
      .then(data => {
        if (!alive) return;
        setEntries(data);
        setProblem(false);
      })
      .catch(() => alive && setProblem(true));
    return () => {
      alive = false;
    };
  }, [monthId, refreshKey]);

  return (
    <WidgetCard
      title="Recent Activity"
      icon={History}
      loading={entries === null && !problem}
    >
      {problem ? (
        <EmptyLine>Activity is unavailable right now.</EmptyLine>
      ) : !entries || entries.length === 0 ? (
        <EmptyLine>No edits this month yet.</EmptyLine>
      ) : (
        <ul className="space-y-2">
          {entries.map(e => {
            const who = e.profiles?.name || e.metadata?.user_name || 'Someone';
            const what = e.column_name ? `“${e.column_name}”` : 'an account';
            const account = e.metadata?.legal_business_name;
            return (
              <li key={e.id} className="text-sm leading-snug">
                <span className="font-medium text-slate-900">{who}</span>{' '}
                <span className="text-slate-600">
                  {VERBS[e.action_type] ?? 'changed'} {what}
                  {account && (
                    <>
                      {' '}
                      on <span className="text-slate-800">{account}</span>
                    </>
                  )}
                </span>
                <span className="block text-xs text-slate-400">
                  {formatDistanceToNow(new Date(e.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}
